import { Dispatch, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Controller, useForm } from 'react-hook-form';
import MuiGrid from '@mui/material/Grid';
import TextField from 'src/common/components/TextField';
import AutocompleteSelect from 'src/common/components/AutocompleteSelect';
import {
  ErrorObject,
  DEFAULT_ERROR_STATE,
} from 'src/containers/client-assessment/types';
import ErrorText from 'src/common/components/ErrorScreen/ErrorText';
import { mobileBreakpoint } from 'src/containers/client-assessment/mobileBreakpoint';
import { GET_STATES } from './constants';
import { getStateNameByStateCode, isValidZipCode } from './utils';

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  @media ${mobileBreakpoint.mobile} {
    height: 40vh;
    overflow-y: scroll;
  }
`;

const Grid = styled(MuiGrid)`
  width: 100%;
  @media ${mobileBreakpoint.mobile} {
    padding-top: 20px !important;
    padding-left: 2px !important;
    margin-left: 0 !important;
  }
`;

const HelperText = styled.p`
  font-size: 12px;
  color: ${props => props.theme.main.primary};
  margin-left: 40px;
`;
const HelperTextContainer = styled.span`
  @media ${mobileBreakpoint.mobile} {
    display: none;
  }
`;

const Label = styled.label`
  color: '#101828';
  font: 400 0.875rem/1.5rem Inter, sans-serif;
`;

type Props = {
  selectedQuestionId: string;
  questionnaireValue: any;
  setQuestionnaireValue: Dispatch<any>;
  error: ErrorObject;
  setError: Dispatch<ErrorObject>;
};

export default function Address({
  selectedQuestionId,
  questionnaireValue,
  setQuestionnaireValue,
  error,
  setError,
}: Props) {
  const questionValue = questionnaireValue[selectedQuestionId];
  const [states, setStates] = useState([]);
  const [statesData, setStatesData] = useState([]);
  const { watch, control, reset, setValue } = useForm({
    mode: 'all',
  });
  const watchFields = watch();

  useEffect(() => {
    formChanged();
    getStates();
    if (questionValue) {
      if (typeof questionValue === 'object') {
        reset({
          ...questionValue,
          state: { label: questionValue.state, value: questionValue.state },
        });
      } else {
        reset({
          // line1: questionValue?.line1 ?? '',
          // line2: questionValue?.line2 ?? '',
          city: questionValue?.city ?? '',
          state:
            {
              label: questionValue?.state?.replace(' ', ''),
              value: questionValue?.state?.replace(' ', ''),
            } ?? '',
          zipcode: questionValue?.zipcode?.replace(' ', '') ?? '',
        });
      }
    } else reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getStates = async () => {
    const statesResponse = await fetch(GET_STATES)
      .then(response => response.json())
      .then(response => response?.data?.states ?? []);
    setStatesData(statesResponse ?? []);
    const transformedStateResponse = statesResponse.map(state => ({
      label: state?.name,
      value: state?.name,
    }));

    setStates(transformedStateResponse);
  };

  const validateErrorFields = () => {
    switch (true) {
      case !watchFields.zipcode || watchFields.zipcode?.length !== 5:
        return {
          message: 'ZIP Code is required to proceed.',
          valid: true,
        };
      default:
        return null;
    }
  };

  const handleSubmission = () => {
    const watchFields = watch();
    const inputError = validateErrorFields();
    if (inputError) {
      setQuestionnaireValue({
        ...questionnaireValue,
        [selectedQuestionId]: {},
      });
      setError(inputError);
    } else {
      setQuestionnaireValue({
        ...questionnaireValue,
        // [selectedQuestionId]: `${watchFields?.line1}, ${watchFields?.line2}, ${watchFields?.city}, ${watchFields?.state?.value}, ${watchFields?.zipcode},`,
        [selectedQuestionId]: {
          ...watchFields,
        },
      });
      setError(DEFAULT_ERROR_STATE);
    }
  };

  const formChanged = () => {
    handleSubmission();
  };

  function extractZipCode(address) {
    const pattern = /\b\d{5}\b/;
    const match = address?.match(pattern);
    if (match) return match[0];
    return '';
  }

  function getStateFromZipCode() {
    let zip = isValidZipCode(watchFields?.zipcode ?? '');
    if (zip) {
      let val = getStateNameByStateCode(zip?.state, statesData);
      setValue('state', val);
      return;
    }
    setError({ message: 'Invalid zip code', valid: true });
    setValue('state', { label: '', value: '' });
  }

  function zipCodeErrorMessage() {
    if (!watchFields?.zipcode || watchFields?.zipcode?.length !== 5)
      return 'ZIP Code is required to proceed.';
    if (error?.valid) return 'Invalid zip code.';
  }

  useEffect(() => {
    if (watchFields?.zipcode && statesData) {
      formChanged();
      getStateFromZipCode();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchFields.zipcode, statesData]);

  useEffect(() => {
    if (questionValue && typeof questionValue === 'string') {
      const zipcode = extractZipCode(questionValue);

      let addressArray = questionValue?.split(',')?.map(item => item.trim());
      const address = {
        // line1: addressArray[0] ?? '',
        // line2: addressArray[1] ?? '',
        city: addressArray[0] ?? '',
        state:
          {
            label: addressArray[1]?.replace(' ', '') ?? '',
            value: addressArray[1]?.replace(' ', '') ?? '',
          } ?? '',
        zipcode: zipcode?.replace(' ', '') ?? '',
      };

      reset(address);
      getStateFromZipCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionValue]);

  return (
    <Wrapper>
      <form>
        <Grid container spacing={5} alignItems="flex-start">
          {/* <Grid item sm={6} xs={12}>
            <Controller
              name="line1"
              control={control}
              rules={{ required: false }}
              render={({ field: { value, onChange } }) => (
                <TextField
                  onChange={({ target }) => {
                    onChange(target.value);
                  }}
                  label="Address Line 1"
                  fullWidth
                  value={value}
                  onBlur={formChanged}
                />
              )}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Controller
              name="line2"
              control={control}
              rules={{ required: false }}
              render={({ field: { value, onChange } }) => (
                <TextField
                  onChange={({ target }) => {
                    onChange(target.value);
                  }}
                  label="Address Line 2"
                  fullWidth
                  value={value}
                  onBlur={formChanged}
                />
              )}
            />
          </Grid> */}
          <Grid item sm={6} xs={12}>
            <Controller
              name="city"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <>
                  <TextField
                    onChange={({ target }) => onChange(target.value)}
                    label="City"
                    fullWidth
                    value={value}
                    onBlur={formChanged}
                  />
                  {/* <ErrorText>
                    {!watchFields?.city && 'City is required to proceed.'}
                  </ErrorText> */}
                </>
              )}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <Label>State</Label>
            <Controller
              name="state"
              control={control}
              rules={{ required: true }}
              // defaultValue={null}
              render={({ field: { value, onChange } }) => (
                <>
                  <AutocompleteSelect
                    value={value}
                    setValue={e => {
                      onChange(e);
                      formChanged();
                    }}
                    placeholder="Select State"
                    options={states}
                    size="small"
                  />
                  {/* <ErrorText>
                    {!watchFields?.state?.value &&
                      'State is required to proceed.'}
                  </ErrorText> */}
                </>
              )}
            />
          </Grid>
          <Grid item sm={6} xs={12} style={{ minHeight: '9em' }}>
            <Controller
              name="zipcode"
              control={control}
              rules={{ required: true, minLength: 5, maxLength: 5 }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <>
                  <TextField
                    onChange={({ target }) => onChange(target.value)}
                    label="ZIP Code*"
                    fullWidth
                    value={value}
                    type="number"
                    style={{ marginBottom: '6px' }}
                    onBlur={formChanged}
                  />
                  <ErrorText>{zipCodeErrorMessage() ?? ''}</ErrorText>
                </>
              )}
            />
          </Grid>
          {/* <HelperTextContainer>
            <Grid item sm={12} xs={12} style={{ minHeight: '4em' }}>
              <HelperText>
                *Don’t worry, we aren’t going to send you any mail! Giving us
                your address helps us quickly look up information for your
                advisor to speed up this process.
              </HelperText>
            </Grid>
          </HelperTextContainer> */}

          {/* <Grid item xs={3}>
          <TextField
            onChange={({ target }) => onChangeInput('landmark', target.value)}
            label="Landmark"
            fullWidth
          />
        </Grid> */}
          {/* <Grid item xs={12}>
          <button style={{ display: 'none' }} type="submit">
            Submit
          </button>
        </Grid> */}
        </Grid>
      </form>
    </Wrapper>
  );
}
